/**
 * ReplyDelay — пауза перед ответом бота клиента (боевая настройка, не тест).
 *
 * Что делает: всё, что клиент напишет внутри окна, склеивается и уходит боту
 * ОДНИМ ходом. Без паузы Тоня отвечает на каждое сообщение отдельно, и клиент,
 * пишущий мысль в три реплики, получает три ответа — то, что выдаёт робота.
 *
 * Значение живёт в реестре Тони (`tenant_bots.reply_delay_sec`), кабинет ходит
 * туда через admin-ручку: это РАЗНЫЕ базы, писать напрямую нельзя.
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Clock, Loader2 } from 'lucide-react'
import { replyDelayApi } from '@/api/client'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

/** Шкала от 5 секунд с шагом 5 — та же, что в тестовом чате. */
const PRESETS = [5, 10, 15, 20, 25, 30, 35, 40]

export function ReplyDelay({ dealId }: { dealId: string }) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['reply-delay', dealId],
    queryFn: async () => {
      if (USE_MOCK) return { reply_delay_sec: null, available: false }
      return replyDelayApi.get(dealId)
    },
    staleTime: 30 * 1000,
  })

  async function pick(sec: number | null) {
    setSaving(true)
    setError(null)
    try {
      if (!USE_MOCK) await replyDelayApi.set(dealId, sec)
      await qc.invalidateQueries({ queryKey: ['reply-delay', dealId] })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить.')
    } finally {
      setSaving(false)
    }
  }

  const current = data?.reply_delay_sec ?? null

  return (
    <div className="rounded-lg border p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Пауза перед ответом</span>
        {(isLoading || saving) && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed">
        Бот ждёт указанное время и отвечает один раз на всё, что клиент успел
        написать. Без паузы — ответ на каждое сообщение отдельно.
      </p>

      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          disabled={saving}
          onClick={() => pick(null)}
          className={`rounded border px-2.5 py-1 text-xs ${
            current === null ? 'bg-accent font-medium' : 'text-muted-foreground'
          }`}
        >
          Без паузы
        </button>
        {PRESETS.map((v) => (
          <button
            key={v}
            type="button"
            disabled={saving}
            onClick={() => pick(v)}
            className={`rounded border px-2.5 py-1 text-xs ${
              current === v ? 'bg-accent font-medium' : 'text-muted-foreground'
            }`}
          >
            {v} сек
          </button>
        ))}
      </div>

      {error && <p className="text-xs text-amber-700">{error}</p>}

      {data && !data.available && (
        <p className="text-[11px] text-muted-foreground">
          Кабинет клиента ещё не создан — паузу можно будет задать после этого.
        </p>
      )}
    </div>
  )
}
